/**
 * useOnboardingSteps — Bridge hook between old and new onboarding systems.
 *
 * Returns a normalized list of onboarding steps regardless of whether the org
 * has configured a template (new) or is using the legacy hardcoded steps (old).
 *
 * Usage:
 *   const { steps, source, loading } = useOnboardingSteps();
 *   const { steps: selfServiceSteps } = useOnboardingSteps({ category: 'self_service' });
 *
 * Each step in the returned array has:
 *   { id, label, description, icon, category, isRequired, isEnabled, sortOrder }
 *
 * The hook also returns helper functions for working with progress data:
 *   - getStepProgress(progressData, stepId) → status string
 *   - getCompletionStats(progressData) → { completed, required, total, allRequiredDone }
 *   - isComplete(progressData) → boolean
 */

import { useMemo } from 'react';
import { useData } from '../context/DataProvider';
import { OLD_STEPS, getApplicableSteps, isOnboardingStepApplicable } from '../utils/onboardingSteps';

/**
 * @param {Object} options
 * @param {string} [options.category] - Filter by category: 'self_service', 'admin_managed', 'integration', 'system'
 * @param {Object} [options.job] - Job object for conditional step filtering (e.g., uses_pipelines)
 */
export default function useOnboardingSteps(options = {}) {
  const { category, job } = options;
  const { onboardingTemplate, stepLibrary, loading: dataLoading } = useData();

  const result = useMemo(() => {
    const hasTemplate = onboardingTemplate && onboardingTemplate.length > 0;

    let steps;
    let source;

    if (hasTemplate) {
      // ─── New world: org has configured a template ─────────────
      source = 'template';
      steps = onboardingTemplate
        .filter(t => t.is_enabled)
        .map(t => {
          // t.step is the joined step_library record (from the DataProvider query: select('*, step:step_id(*)'))
          const libStep = t.step || stepLibrary?.find(s => s.id === t.step_id) || {};
          return {
            id: t.step_id,
            label: libStep.label || t.step_id,
            description: libStep.description || '',
            icon: libStep.icon || '📋',
            category: libStep.category || 'admin_managed',
            isRequired: t.is_required,
            isEnabled: t.is_enabled,
            sortOrder: t.sort_order,
            config: t.config || {},
            // Legacy compatibility: the 'key' field matches the old column name
            // for steps that map 1:1 (e.g., 'general_onboarding')
            key: t.step_id,
          };
        })
        .sort((a, b) => a.sortOrder - b.sortOrder);

      if (job) {
        steps = steps.filter(step => isOnboardingStepApplicable(step, job));
      }
    } else {
      // ─── Old world: fall back to hardcoded steps ──────────────
      source = 'legacy';
      const applicableSteps = getApplicableSteps(OLD_STEPS, job);
      steps = applicableSteps.map((s, i) => ({
        id: s.key,
        label: s.label,
        description: '',
        icon: s.icon || '📋',
        category: s.category || 'admin_managed',
        isRequired: true, // all old steps were implicitly required
        isEnabled: true,
        sortOrder: (i + 1) * 10,
        config: {},
        key: s.key, // legacy column name
      }));
    }

    // Apply category filter if requested
    if (category) {
      steps = steps.filter(s => s.category === category);
    }

    return { steps, source };
  }, [onboardingTemplate, stepLibrary, category, job]);

  // ─── Helper functions ──────────────────────────────────────

  /**
   * Get the status of a specific step from progress data.
   *
   * Handles both formats:
   *   - Old format (flat columns): progressData = { general_onboarding: 'Completed', ... }
   *   - New format (array of rows): progressData = [{ step_id: 'general_onboarding', status: 'Completed' }, ...]
   */
  function getStepProgress(progressData, stepId) {
    if (!progressData) return 'Not Started';

    // New format: array of progress rows
    if (Array.isArray(progressData)) {
      const row = progressData.find(p => p.step_id === stepId);
      return row?.status || 'Not Started';
    }

    // Old format: flat columns
    // Map new step IDs back to old column names if needed
    const oldKey = stepId; // For most steps, the ID matches the column name
    return progressData[oldKey] || 'Not Started';
  }

  /**
   * Get completion statistics from progress data.
   */
  function getCompletionStats(progressData) {
    const { steps } = result;
    let completed = 0;
    let required = 0;
    let total = steps.length;

    for (const step of steps) {
      const status = getStepProgress(progressData, step.id);
      if (status === 'Completed') completed++;
      if (step.isRequired) required++;
    }

    const requiredCompleted = steps
      .filter(s => s.isRequired)
      .filter(s => getStepProgress(progressData, s.id) === 'Completed')
      .length;

    return {
      completed,
      total,
      required,
      requiredCompleted,
      allRequiredDone: requiredCompleted === required,
      allDone: completed === total,
      percentage: total > 0 ? Math.round((completed / total) * 100) : 0,
    };
  }

  /**
   * Check if all required steps are complete.
   */
  function isComplete(progressData) {
    return getCompletionStats(progressData).allRequiredDone;
  }

  return {
    steps: result.steps,
    source: result.source,
    loading: dataLoading,

    // Helpers
    getStepProgress,
    getCompletionStats,
    isComplete,

    // Raw data access (for advanced use)
    stepLibrary: stepLibrary || [],
    onboardingTemplate: onboardingTemplate || [],
  };
}
